import {SECTION_WISE_SAVED_FIELD_MAPPING} from 'src/interfaces/Platform.interface'

type PayoutCostsMappingMeta = {
  api_code?: string
  field_code?: string
  display_name?: string
  platform?: number
  id?: number
  setting_mapping_id?: number | null
  isDefault?: boolean
  isRequired?: boolean
  map_with?: PayoutCostsMappingMeta | null
  [key: string]: any
}

const resolvePayoutFieldMappingSlotId = (
  mapping?: PayoutCostsMappingMeta | null
): string => {
  if (!mapping) return ''
  const slotId = mapping.setting_mapping_id ?? mapping.id
  return slotId !== undefined && slotId !== null ? `${slotId}` : ''
}

export const buildPayoutFieldMappingDedupeKey = (
  mapping?: PayoutCostsMappingMeta | null
): string => {
  const fieldCode = `${mapping?.field_code ?? mapping?.api_code ?? ''}`.trim()
  if (!fieldCode) return ''

  const isDefault = mapping?.isDefault !== false
  const slotId = resolvePayoutFieldMappingSlotId(mapping)

  if (
    !isDefault &&
    (fieldCode === 'DEBIT_ACCOUNT' ||
      fieldCode === 'PAYOUT_ACCOUNT' ||
      fieldCode === 'PAYOUT_COSTS')
  ) {
    return `${fieldCode}::false::${slotId || '0'}`
  }

  return `${fieldCode}::${isDefault}`
}

const dedupePayoutFieldMappingsByCode = (fieldMappings: PayoutCostsMappingMeta[] = []) => {
  const deduped = new Map<string, PayoutCostsMappingMeta>()
  fieldMappings.forEach((mapping) => {
    const key = buildPayoutFieldMappingDedupeKey(mapping)
    if (!key || deduped.has(key)) return
    deduped.set(key, mapping)
  })
  return Array.from(deduped.values())
}

export const serializeChildFieldMappingsKey = (
  fieldMappings: PayoutCostsMappingMeta[] = []
): string =>
  fieldMappings
    .map(
      (mapping) =>
        `${mapping.field_code}:${Boolean(mapping.isDefault)}:${mapping.setting_mapping_id ?? mapping.id ?? ''}`
    )
    .sort()
    .join('|')

export const serializeOrderMappingsKey = (orderMappings: PayoutCostsMappingMeta[] = []): string =>
  orderMappings.map((mapping) => mapping.api_code ?? mapping.field_code ?? '').join(',')

export const serializeSavedPayoutMappingsKey = (
  savedMappings: SECTION_WISE_SAVED_FIELD_MAPPING[] = [],
  settingId?: number | null
): string =>
  (Array.isArray(savedMappings) ? savedMappings : [])
    .filter((mapping) => !settingId || mapping.setting_id === settingId)
    .map(
      (mapping) =>
        `${mapping.id}:${mapping.setting_id}:${mapping.source_field?.api_code ?? ''}:${mapping.destination_field?.api_code ?? ''}:${mapping.setting_mapping_id ?? ''}:${Boolean(mapping.isDefault)}`
    )
    .sort()
    .join('|')

export const findAllSavedPayoutCostsEntries = (
  savedMappings: SECTION_WISE_SAVED_FIELD_MAPPING[] | [] = [],
  settingId?: number | null
): SECTION_WISE_SAVED_FIELD_MAPPING[] => {
  const payoutEntries = (Array.isArray(savedMappings) ? savedMappings : []).filter(
    (mapping) => mapping?.source_field?.api_code === 'PAYOUT_COSTS'
  )
  if (!settingId) return payoutEntries
  return payoutEntries.filter((mapping) => mapping.setting_id === settingId)
}

export const findSavedPayoutCostsEntry = (
  savedMappings: SECTION_WISE_SAVED_FIELD_MAPPING[] | [] = [],
  settingId?: number | null
): SECTION_WISE_SAVED_FIELD_MAPPING | null => {
  const payoutEntries = findAllSavedPayoutCostsEntries(savedMappings, settingId)
  if (!payoutEntries.length) return null
  return payoutEntries[0] ?? null
}

export const findSavedPayoutCostsEntryBySettingMappingId = (
  savedMappings: SECTION_WISE_SAVED_FIELD_MAPPING[] | [] = [],
  settingMappingId?: number | null,
  settingId?: number | null
): SECTION_WISE_SAVED_FIELD_MAPPING | null => {
  if (settingMappingId === undefined || settingMappingId === null) {
    return findSavedPayoutCostsEntry(savedMappings, settingId)
  }

  return (
    findAllSavedPayoutCostsEntries(savedMappings, settingId).find(
      (mapping) => mapping.setting_mapping_id === settingMappingId
    ) ?? null
  )
}

export const findDependentSavedFieldMapping = (
  sourceApiCode: string,
  destinationApiCode: string,
  savedMappings: SECTION_WISE_SAVED_FIELD_MAPPING[] = [],
  settingId?: number | null,
  settingMappingId?: number | null
): SECTION_WISE_SAVED_FIELD_MAPPING | null => {
  const matches = savedMappings.filter((mapping) => {
    if (mapping?.source_field?.api_code !== sourceApiCode) return false
    if (mapping?.destination_field?.api_code !== destinationApiCode) return false
    if (settingId && mapping?.setting_id && mapping.setting_id !== settingId) return false
    return true
  })

  if (settingMappingId !== undefined && settingMappingId !== null) {
    return (
      matches.find((mapping) => mapping.setting_mapping_id === settingMappingId) ??
      matches.find(
        (mapping) =>
          mapping.setting_mapping_id === undefined || mapping.setting_mapping_id === null
      ) ??
      null
    )
  }

  return matches[0] ?? null
}

export const findSavedPayoutCostsSourceField = (
  savedMappings: SECTION_WISE_SAVED_FIELD_MAPPING[] | [] = [],
  settingId?: number | null
): PayoutCostsMappingMeta | null =>
  findSavedPayoutCostsEntry(savedMappings, settingId)?.source_field ?? null

export const findPayoutCostsInOrderMappings = (
  orderMappings: PayoutCostsMappingMeta[] = []
): PayoutCostsMappingMeta | null =>
  orderMappings.find((mapping) => mapping?.api_code === 'PAYOUT_COSTS') ?? null

export const PAYOUT_PAIR_DESTINATION_FIELD_CODES = [
  'DEBIT_ACCOUNT',
  'PAYOUT_ACCOUNT',
] as const

export const isPayoutPairDestinationField = (fieldCode?: string | null): boolean =>
  Boolean(
    fieldCode &&
      PAYOUT_PAIR_DESTINATION_FIELD_CODES.includes(
        fieldCode as (typeof PAYOUT_PAIR_DESTINATION_FIELD_CODES)[number]
      )
  )

export const matchesPayoutPairSlotForChannel = (
  mapping?: PayoutCostsMappingMeta | null,
  relatedAvailablePlatformId?: number | string | null
): boolean => {
  if (!mapping) {
    return false
  }

  const fieldCode = mapping.field_code ?? mapping.api_code
  if (!isPayoutPairDestinationField(fieldCode)) {
    return true
  }

  const slotRelatedPlatform = mapping.related_available_platform
  if (
    slotRelatedPlatform === null ||
    slotRelatedPlatform === undefined ||
    slotRelatedPlatform === ''
  ) {
    return true
  }

  if (
    relatedAvailablePlatformId === null ||
    relatedAvailablePlatformId === undefined ||
    relatedAvailablePlatformId === ''
  ) {
    return true
  }

  return `${slotRelatedPlatform}` === `${relatedAvailablePlatformId}`
}

const findPayoutSchemaSlotMeta = (
  mergedSchema: PayoutCostsMappingMeta[] = [],
  slotId?: number | null
): PayoutCostsMappingMeta | null =>
  mergedSchema.find(
    (mapping) =>
      `${mapping.setting_mapping_id ?? mapping.id ?? ''}` === `${slotId ?? ''}`
  ) ?? null

export const PAYOUT_DEFAULT_ACCOUNT_FIELD_CODES = [
  'CREDIT_ACCOUNT',
  'BANK_DEPOSIT_HEADER_ACCOUNT',
  'BANK_DEPOSIT_LINE_ACCOUNT',
  'CUSTOMER_FOR_PAYOUT',
  'PAYMENT_METHOD',
] as const

export const isPayoutDefaultAccountField = (fieldCode?: string | null): boolean =>
  Boolean(
    fieldCode &&
      PAYOUT_DEFAULT_ACCOUNT_FIELD_CODES.includes(
        fieldCode as (typeof PAYOUT_DEFAULT_ACCOUNT_FIELD_CODES)[number]
      )
  )

export const isPayoutRelatedFieldCode = (fieldCode?: string | null): boolean =>
  Boolean(
    fieldCode &&
      (fieldCode === 'PAYOUT_COSTS' ||
        isPayoutPairDestinationField(fieldCode) ||
        isPayoutDefaultAccountField(fieldCode))
  )

export const resolveOrderSettingContainerKey = (groupCode?: string | null): string | null => {
  if (!groupCode) {
    return null
  }

  const normalized = groupCode.replace(/_setting$/i, '').toUpperCase()
  switch (normalized) {
    case 'ORDER':
      return 'order_setting'
    case 'ACCOUNTING_ORDER':
      return 'accounting_order_setting'
    case 'ERP_ORDER':
      return 'erp_order_setting'
    case 'OMS_ORDER':
      return 'oms_order_setting'
    case 'ORDER_PAYOUT':
      return 'order_payout_setting'
    default:
      return null
  }
}

export const isOrderPayoutSyncEnabled = (
  normalizedValues: Record<string, any> | null | undefined,
  groupCode?: string | null
): boolean => {
  const containerKey = resolveOrderSettingContainerKey(groupCode)
  if (!containerKey) {
    return true
  }

  return Boolean(normalizedValues?.[containerKey]?.isImportOrderPayout)
}

/** Match save validation to render rules: payout mappings matter only when sync is on. */
export const shouldValidatePayoutMapping = (
  normalizedValues: Record<string, any> | null | undefined,
  groupCode: string | null | undefined,
  fieldCode?: string | null
): boolean => {
  if (!isPayoutRelatedFieldCode(fieldCode)) {
    return true
  }

  return isOrderPayoutSyncEnabled(normalizedValues, groupCode)
}

export const hasFullPayoutErpSchema = (
  fieldMappings: PayoutCostsMappingMeta[] = []
): boolean =>
  PAYOUT_DEFAULT_ACCOUNT_FIELD_CODES.every((code) =>
    fieldMappings.some((mapping) => (mapping.field_code ?? mapping.api_code) === code)
  )

export const hasUsablePayoutChildSchema = (
  fieldMappings: PayoutCostsMappingMeta[] = []
): boolean => {
  if (hasFullPayoutErpSchema(fieldMappings)) {
    return true
  }

  if (!fieldMappings.length) {
    return false
  }

  const fieldCodes = fieldMappings
    .map((mapping) => mapping.field_code ?? mapping.api_code)
    .filter(Boolean)

  if (fieldCodes.length === 1 && fieldCodes[0] === 'PAYOUT_COSTS') {
    return false
  }

  const hasDefaultField = fieldMappings.some((mapping) => {
    const fieldCode = mapping.field_code ?? mapping.api_code
    return (
      mapping.isDefault !== false &&
      fieldCode !== 'PAYOUT_COSTS' &&
      !isPayoutPairDestinationField(fieldCode)
    )
  })

  const hasPayoutPairSlot = fieldMappings.some((mapping) => {
    const fieldCode = mapping.field_code ?? mapping.api_code
    return mapping.isDefault === false && isPayoutPairDestinationField(fieldCode)
  })

  return hasDefaultField && hasPayoutPairSlot
}

export type PayoutSettingsGroup = {
  group_code?: string
  children?: Array<{
    name?: string
    field_mappings?: PayoutCostsMappingMeta[]
  }>
  mappings_data?: PayoutCostsMappingMeta[]
}

const resolvePayoutSchemaGroupCode = (
  effectiveSourceGroupCode?: string | null
): string => {
  if (effectiveSourceGroupCode === 'ACCOUNTING_ORDER') return 'ACCOUNTING_ORDER'
  if (effectiveSourceGroupCode === 'OMS_ORDER') return 'OMS_ORDER'
  return 'ERP_ORDER'
}

export const resolvePayoutIntegrationPlatformSettings = ({
  effectiveSourceGroupCode,
  erpPlatformSettings = [],
  accountingPlatformSettings = [],
  omsPlatformSettings = [],
}: {
  effectiveSourceGroupCode?: string | null
  erpPlatformSettings?: PayoutSettingsGroup[]
  accountingPlatformSettings?: PayoutSettingsGroup[]
  omsPlatformSettings?: PayoutSettingsGroup[]
}): PayoutSettingsGroup[] => {
  if (effectiveSourceGroupCode === 'ACCOUNTING_ORDER') {
    return accountingPlatformSettings
  }
  if (effectiveSourceGroupCode === 'OMS_ORDER') {
    return omsPlatformSettings
  }
  return erpPlatformSettings
}

const pickPayoutChildFieldMappingsFromSettings = (
  settings: PayoutSettingsGroup[] = [],
  groupCode: string
): PayoutCostsMappingMeta[] =>
  settings
    .find((setting) => setting.group_code === groupCode)
    ?.children?.find((child) => child.name === 'isImportOrderPayout')?.field_mappings ?? []

export const resolvePayoutChildFieldMappings = ({
  integrationPlatformSettings = [],
  fallbackSettings = [],
  schemaGroupCode = 'ERP_ORDER',
}: {
  integrationPlatformSettings?: PayoutSettingsGroup[]
  fallbackSettings?: PayoutSettingsGroup[]
  schemaGroupCode?: string
}): PayoutCostsMappingMeta[] => {
  const fromIntegrationStore = pickPayoutChildFieldMappingsFromSettings(
    integrationPlatformSettings,
    schemaGroupCode
  )

  if (hasUsablePayoutChildSchema(fromIntegrationStore)) {
    return fromIntegrationStore
  }

  const fromFallback = pickPayoutChildFieldMappingsFromSettings(
    fallbackSettings,
    schemaGroupCode
  )

  if (hasUsablePayoutChildSchema(fromFallback)) {
    return fromFallback
  }

  return fromIntegrationStore.length >= fromFallback.length
    ? fromIntegrationStore
    : fromFallback
}

export const resolveErpPayoutChildFieldMappings = ({
  erpPlatformSettings = [],
  fallbackSettings = [],
}: {
  erpPlatformSettings?: PayoutSettingsGroup[]
  fallbackSettings?: PayoutSettingsGroup[]
}): PayoutCostsMappingMeta[] =>
  resolvePayoutChildFieldMappings({
    integrationPlatformSettings: erpPlatformSettings,
    fallbackSettings,
    schemaGroupCode: 'ERP_ORDER',
  })

export const resolvePayoutBuildContext = ({
  effectiveSourceGroupCode,
  erpPlatformSettings = [],
  accountingPlatformSettings = [],
  omsPlatformSettings = [],
  fallbackSettings = [],
  childFieldMappings = [],
}: {
  effectiveSourceGroupCode?: string | null
  erpPlatformSettings?: PayoutSettingsGroup[]
  accountingPlatformSettings?: PayoutSettingsGroup[]
  omsPlatformSettings?: PayoutSettingsGroup[]
  fallbackSettings?: PayoutSettingsGroup[]
  childFieldMappings?: PayoutCostsMappingMeta[]
}): {
  schemaGroupCode: string
  integrationPlatformSettings: PayoutSettingsGroup[]
  integrationSchema: PayoutCostsMappingMeta[]
  integrationOrderMappingsData: PayoutCostsMappingMeta[]
  isReady: boolean
} => {
  const schemaGroupCode = resolvePayoutSchemaGroupCode(effectiveSourceGroupCode)
  const integrationPlatformSettings = resolvePayoutIntegrationPlatformSettings({
    effectiveSourceGroupCode,
    erpPlatformSettings,
    accountingPlatformSettings,
    omsPlatformSettings,
  })
  const integrationSchema = resolvePayoutChildFieldMappings({
    integrationPlatformSettings,
    fallbackSettings,
    schemaGroupCode,
  })
  const integrationOrderMappingsData =
    integrationPlatformSettings.find(
      (platformSetting) => platformSetting.group_code === schemaGroupCode
    )?.mappings_data ?? []

  return {
    schemaGroupCode,
    integrationPlatformSettings,
    integrationSchema,
    integrationOrderMappingsData,
    isReady:
      hasUsablePayoutChildSchema(integrationSchema) ||
      hasUsablePayoutChildSchema(childFieldMappings),
  }
}

export const isPayoutChildSchemaReady = ({
  effectiveSourceGroupCode,
  erpPlatformSettings = [],
  accountingPlatformSettings = [],
  omsPlatformSettings = [],
  fallbackSettings = [],
  childFieldMappings = [],
}: {
  effectiveSourceGroupCode?: string | null
  erpPlatformSettings?: PayoutSettingsGroup[]
  accountingPlatformSettings?: PayoutSettingsGroup[]
  omsPlatformSettings?: PayoutSettingsGroup[]
  fallbackSettings?: PayoutSettingsGroup[]
  childFieldMappings?: PayoutCostsMappingMeta[]
}): boolean =>
  resolvePayoutBuildContext({
    effectiveSourceGroupCode,
    erpPlatformSettings,
    accountingPlatformSettings,
    omsPlatformSettings,
    fallbackSettings,
    childFieldMappings,
  }).isReady

export const buildPayoutCostsMappingMeta = (
  payoutFieldMeta?: PayoutCostsMappingMeta | null
): PayoutCostsMappingMeta | null => {
  if (!payoutFieldMeta) return null

  return {
    ...payoutFieldMeta,
    api_code: 'PAYOUT_COSTS',
    field_code: 'PAYOUT_COSTS',
    display_name: payoutFieldMeta.display_name ?? 'Payout cost',
    isDefault: false,
  }
}

/** Same as other one-to-one mappings: copy API isRequired; missing means not required. */
const resolvePayoutPairIsRequired = (
  schemaMeta?: {isRequired?: boolean} | null,
  savedField?: {isRequired?: boolean} | null
): boolean => Boolean(schemaMeta?.isRequired ?? savedField?.isRequired)

const buildPayoutPairDestinationFieldMeta = (
  savedEntry?: SECTION_WISE_SAVED_FIELD_MAPPING | null,
  schemaMeta?: PayoutCostsMappingMeta | null
): PayoutCostsMappingMeta | null => {
  const fieldCode =
    schemaMeta?.field_code ??
    schemaMeta?.api_code ??
    savedEntry?.destination_field?.api_code

  if (!isPayoutPairDestinationField(fieldCode)) {
    return null
  }

  const slotId = schemaMeta?.setting_mapping_id ?? schemaMeta?.id ?? savedEntry?.setting_mapping_id

  if (schemaMeta) {
    return {
      ...schemaMeta,
      field_code: fieldCode,
      api_code: fieldCode,
      display_name: schemaMeta.display_name ?? fieldCode,
      isDefault: false,
      isRequired: resolvePayoutPairIsRequired(schemaMeta, savedEntry?.destination_field),
      setting_mapping_id: slotId ?? null,
    }
  }

  if (!savedEntry?.destination_field) return null

  return {
    field_code: fieldCode,
    api_code: fieldCode,
    display_name: savedEntry.destination_field.display_name ?? fieldCode,
    isDefault: false,
    isRequired: resolvePayoutPairIsRequired(null, savedEntry.destination_field),
    setting_mapping_id: slotId ?? null,
  }
}

const buildDebitAccountFieldMeta = buildPayoutPairDestinationFieldMeta

const buildDebitAccountMapWithFromErpSchema = (
  erpOrderMappingsData: PayoutCostsMappingMeta[] = []
): PayoutCostsMappingMeta | null => {
  const erpDebitSlot = erpOrderMappingsData.find(
    (mapping) =>
      isPayoutPairDestinationField(mapping.field_code ?? mapping.api_code) &&
      mapping.isDefault === false
  )
  if (!erpDebitSlot) return null

  return buildPayoutPairDestinationFieldMeta(null, erpDebitSlot)
}

const collectPayoutPairDestinationSlots = ({
  mergedSchema = [],
  erpOrderMappingsData = [],
  savedPayoutEntries = [],
  relatedAvailablePlatformId = null,
}: {
  mergedSchema?: PayoutCostsMappingMeta[]
  erpOrderMappingsData?: PayoutCostsMappingMeta[]
  savedPayoutEntries?: SECTION_WISE_SAVED_FIELD_MAPPING[]
  relatedAvailablePlatformId?: number | string | null
}): PayoutCostsMappingMeta[] => {
  const slots = new Map<string, PayoutCostsMappingMeta>()

  const register = (meta?: PayoutCostsMappingMeta | null) => {
    if (!meta) return
    if (!matchesPayoutPairSlotForChannel(meta, relatedAvailablePlatformId)) return

    const fieldCode = meta.field_code ?? meta.api_code
    if (!isPayoutPairDestinationField(fieldCode)) return

    const normalized = buildPayoutPairDestinationFieldMeta(null, meta)
    if (!normalized) return

    const key = resolvePayoutFieldMappingSlotId(normalized) || `pair-${slots.size}`
    if (!slots.has(key)) {
      slots.set(key, normalized)
    }
  }

  const schemaPairSlots = [...mergedSchema, ...erpOrderMappingsData].filter(
    (mapping) =>
      isPayoutPairDestinationField(mapping.field_code ?? mapping.api_code) &&
      mapping.isDefault === false
  )
  const hasChannelSpecificSlot = schemaPairSlots.some((mapping) => {
    const relatedPlatform = mapping.related_available_platform
    return (
      relatedAvailablePlatformId !== null &&
      relatedAvailablePlatformId !== undefined &&
      relatedAvailablePlatformId !== '' &&
      relatedPlatform !== null &&
      relatedPlatform !== undefined &&
      relatedPlatform !== '' &&
      `${relatedPlatform}` === `${relatedAvailablePlatformId}`
    )
  })

  schemaPairSlots
    .filter((mapping) => {
      if (!hasChannelSpecificSlot) {
        return matchesPayoutPairSlotForChannel(mapping, relatedAvailablePlatformId)
      }

      const relatedPlatform = mapping.related_available_platform
      return (
        relatedPlatform !== null &&
        relatedPlatform !== undefined &&
        relatedPlatform !== '' &&
        `${relatedPlatform}` === `${relatedAvailablePlatformId}`
      )
    })
    .forEach(register)

  const hasSchemaPairSlots = slots.size > 0
  savedPayoutEntries.forEach((entry) => {
    // Saved mappings provide a fallback only when the settings schema has no
    // payout destination slot. Otherwise stale/source-side IDs can create
    // duplicate sections alongside the configured destination slot.
    if (hasSchemaPairSlots) return

    const slotId = entry.setting_mapping_id
    const schemaSlotMeta = findPayoutSchemaSlotMeta(mergedSchema, slotId)
    if (
      schemaSlotMeta &&
      !matchesPayoutPairSlotForChannel(schemaSlotMeta, relatedAvailablePlatformId)
    ) {
      return
    }

    const key = slotId !== undefined && slotId !== null ? `${slotId}` : `saved-${entry.id}`
    if (slots.has(key)) return

    const destinationCode = entry.destination_field?.api_code
    if (!isPayoutPairDestinationField(destinationCode)) return

    slots.set(
      key,
      buildPayoutPairDestinationFieldMeta(entry, {
        ...(schemaSlotMeta ?? {}),
        field_code: destinationCode,
        api_code: destinationCode,
        display_name: entry.destination_field?.display_name ?? destinationCode,
        setting_mapping_id: slotId ?? null,
        isDefault: false,
        isRequired: resolvePayoutPairIsRequired(
          schemaSlotMeta,
          entry.destination_field
        ),
      })!
    )
  })

  return Array.from(slots.values())
}

const collectDebitAccountSlots = collectPayoutPairDestinationSlots

const buildPayoutCostsWithMapWith = ({
  basePayoutMeta,
  debitSlot,
  savedPayoutEntry,
}: {
  basePayoutMeta: PayoutCostsMappingMeta
  debitSlot: PayoutCostsMappingMeta
  savedPayoutEntry?: SECTION_WISE_SAVED_FIELD_MAPPING | null
}): PayoutCostsMappingMeta => {
  const slotId = debitSlot.setting_mapping_id ?? debitSlot.id ?? null
  const pairFieldCode =
    debitSlot.field_code ?? debitSlot.api_code ?? 'DEBIT_ACCOUNT'
  const resolvedMapWith = savedPayoutEntry?.destination_field
    ? {
        ...savedPayoutEntry.destination_field,
        ...debitSlot,
        field_code: pairFieldCode,
        api_code: pairFieldCode,
        display_name:
          debitSlot.display_name ??
          savedPayoutEntry.destination_field.display_name ??
          pairFieldCode,
        isDefault: false,
        setting_mapping_id: slotId,
      }
    : debitSlot

  if (!savedPayoutEntry?.source_field) {
    return {
      ...basePayoutMeta,
      field_code: 'PAYOUT_COSTS',
      api_code: 'PAYOUT_COSTS',
      display_name: basePayoutMeta.display_name ?? 'Payout cost',
      isDefault: false,
      isRequired: Boolean(basePayoutMeta.isRequired),
      setting_mapping_id: slotId,
      map_with: resolvedMapWith,
    }
  }

  return {
    ...savedPayoutEntry.source_field,
    ...basePayoutMeta,
    field_code: 'PAYOUT_COSTS',
    api_code: 'PAYOUT_COSTS',
    display_name:
      basePayoutMeta.display_name ??
      savedPayoutEntry.source_field.display_name ??
      'Payout cost',
    isDefault: false,
    isRequired: Boolean(basePayoutMeta.isRequired),
    setting_mapping_id: slotId,
    map_with: resolvedMapWith,
  }
}

const attachPayoutCostsMappings = ({
  mappings,
  debitSlots,
  savedPayoutEntries,
  payoutFieldMeta,
}: {
  mappings: PayoutCostsMappingMeta[]
  debitSlots: PayoutCostsMappingMeta[]
  savedPayoutEntries: SECTION_WISE_SAVED_FIELD_MAPPING[]
  payoutFieldMeta?: PayoutCostsMappingMeta | null
}): PayoutCostsMappingMeta[] => {
  const withoutPayoutPairFields = mappings.filter((mapping) => {
    const fieldCode = mapping.field_code ?? mapping.api_code
    return !isPayoutPairDestinationField(fieldCode) && fieldCode !== 'PAYOUT_COSTS'
  })
  const payoutMetas = mappings.filter((mapping) => mapping.field_code === 'PAYOUT_COSTS')
  const basePayoutMeta =
    buildPayoutCostsMappingMeta(
      payoutMetas[0] ?? payoutFieldMeta ?? {field_code: 'PAYOUT_COSTS'}
    ) ?? {
      field_code: 'PAYOUT_COSTS',
      api_code: 'PAYOUT_COSTS',
      display_name: 'Payout cost',
      isDefault: false,
    }

  if (!debitSlots.length) {
    return [...withoutPayoutPairFields, basePayoutMeta]
  }

  const payoutMappings = debitSlots.map((debitSlot) => {
    const slotId = debitSlot.setting_mapping_id ?? debitSlot.id ?? null
    const savedEntry =
      findSavedPayoutCostsEntryBySettingMappingId(savedPayoutEntries, slotId) ??
      savedPayoutEntries.find((entry) => {
        if (slotId === null) return false
        return `${entry.setting_mapping_id ?? ''}` === `${slotId}`
      }) ??
      null

    return buildPayoutCostsWithMapWith({
      basePayoutMeta,
      debitSlot,
      savedPayoutEntry: savedEntry,
    })
  })

  return [...withoutPayoutPairFields, ...payoutMappings]
}

const normalizePayoutSchemaFieldMappings = (
  fieldMappings: PayoutCostsMappingMeta[] = []
): PayoutCostsMappingMeta[] =>
  fieldMappings.map((mapping) => {
    const slotId = mapping.setting_mapping_id ?? mapping.id ?? null
    return {
      ...mapping,
      field_code: mapping.field_code ?? mapping.api_code,
      api_code: mapping.api_code ?? mapping.field_code,
      setting_mapping_id:
        mapping.isDefault === false && slotId !== null ? slotId : mapping.setting_mapping_id ?? null,
    }
  })

export const isPayoutSchemaAwaitingErpFields = ({
  schemaFieldMappings = [],
  erpSchemaFieldMappings = [],
}: {
  schemaFieldMappings?: PayoutCostsMappingMeta[]
  erpSchemaFieldMappings?: PayoutCostsMappingMeta[]
}): boolean => {
  if (hasUsablePayoutChildSchema(schemaFieldMappings)) {
    return false
  }

  if (hasUsablePayoutChildSchema(erpSchemaFieldMappings)) {
    return false
  }

  const orderOnlySchema =
    schemaFieldMappings.length > 0 &&
    schemaFieldMappings.every(
      (mapping) => (mapping.field_code ?? mapping.api_code) === 'PAYOUT_COSTS'
    )

  return orderOnlySchema
}

export const resolvePayoutCostsMapping = ({
  orderMappings = [],
  savedMappings = [],
  payoutFieldMeta = null,
  settingId = null,
}: {
  orderMappings?: PayoutCostsMappingMeta[]
  savedMappings?: SECTION_WISE_SAVED_FIELD_MAPPING[] | []
  payoutFieldMeta?: PayoutCostsMappingMeta | null
  settingId?: number | null
}): PayoutCostsMappingMeta | null => {
  const fromOrderMappings = findPayoutCostsInOrderMappings(orderMappings)
  if (fromOrderMappings) return fromOrderMappings

  const scopedSavedSource = findSavedPayoutCostsSourceField(savedMappings, settingId)
  if (scopedSavedSource) return scopedSavedSource

  const anySavedSource = findSavedPayoutCostsSourceField(savedMappings)
  if (anySavedSource) return anySavedSource

  return buildPayoutCostsMappingMeta(payoutFieldMeta)
}

export const resolvePayoutMappingPlatformSides = ({
  savedMappings = [],
  payoutCostsMapping = null,
  selectedPlatformId,
  sourceStoreData,
  mainStoreData,
  isErpSelectedView = false,
  settingId = null,
}: {
  savedMappings?: SECTION_WISE_SAVED_FIELD_MAPPING[] | []
  payoutCostsMapping?: PayoutCostsMappingMeta | null
  selectedPlatformId?: number | string | null
  sourceStoreData?: {id?: number | string | null} | null
  mainStoreData?: {id?: number | string | null} | null
  isErpSelectedView?: boolean
  settingId?: number | null
}) => {
  const savedEntry =
    findSavedPayoutCostsEntry(savedMappings, settingId) ??
    findSavedPayoutCostsEntry(savedMappings)
  const selectedId = Number(selectedPlatformId)
  const erpStoreId = Number(sourceStoreData?.id)
  const mainStoreId = Number(mainStoreData?.id)

  const sourcePlatformId =
    savedEntry?.source_field?.platform ??
    payoutCostsMapping?.platform ??
    (isErpSelectedView && mainStoreId ? mainStoreId : selectedId || undefined)

  const destinationPlatformId =
    savedEntry?.destination_field?.platform ??
    (erpStoreId || undefined) ??
    (isErpSelectedView ? selectedId : erpStoreId || undefined)

  return {
    sourcePlatformId,
    destinationPlatformId,
  }
}

export const mergePayoutPlatformFieldSide = ({
  fetchedField = null,
  savedField = null,
  meta = null,
  platformId = null,
}: {
  fetchedField?: PayoutCostsMappingMeta | null
  savedField?: PayoutCostsMappingMeta | null
  meta?: PayoutCostsMappingMeta | null
  platformId?: number | string | null
}): PayoutCostsMappingMeta | null => {
  const fieldCode = meta?.field_code ?? meta?.api_code ?? savedField?.api_code ?? fetchedField?.api_code
  if (!fieldCode) return null

  const normalizedPlatformId = (() => {
    const candidate = platformId ?? fetchedField?.platform ?? savedField?.platform
    if (candidate === null || candidate === undefined || candidate === '') return undefined
    const parsed = Number(candidate)
    return Number.isNaN(parsed) ? undefined : parsed
  })()

  if (fetchedField?.id) {
    return {
      ...fetchedField,
      api_code: fieldCode,
      field_code: fieldCode,
      display_name: meta?.display_name ?? fetchedField.display_name,
      platform: fetchedField.platform ?? normalizedPlatformId,
      platformfielddata_set: fetchedField.platformfielddata_set ?? [],
    }
  }

  if (savedField?.id) {
    return {
      ...savedField,
      api_code: savedField.api_code ?? fieldCode,
      field_code: fieldCode,
      display_name: meta?.display_name ?? savedField.display_name,
      platform: savedField.platform ?? normalizedPlatformId,
      platformfielddata_set: savedField.platformfielddata_set ?? [],
    }
  }

  return null
}

export const buildPayoutChildFieldMappings = ({
  schemaFieldMappings = [],
  erpSchemaFieldMappings = [],
  erpOrderMappingsData = [],
  savedMappings = [],
  settingId = null,
  payoutFieldMeta = null,
  relatedAvailablePlatformId = null,
}: {
  schemaFieldMappings?: PayoutCostsMappingMeta[]
  erpSchemaFieldMappings?: PayoutCostsMappingMeta[]
  erpOrderMappingsData?: PayoutCostsMappingMeta[]
  savedMappings?: SECTION_WISE_SAVED_FIELD_MAPPING[] | []
  settingId?: number | null
  payoutFieldMeta?: PayoutCostsMappingMeta | null
  relatedAvailablePlatformId?: number | string | null
}): PayoutCostsMappingMeta[] => {
  if (
    isPayoutSchemaAwaitingErpFields({
      schemaFieldMappings,
      erpSchemaFieldMappings,
    })
  ) {
    return []
  }

  const mergedSchema = dedupePayoutFieldMappingsByCode(
    normalizePayoutSchemaFieldMappings([...schemaFieldMappings, ...erpSchemaFieldMappings])
  )

  const scopedSaved = settingId
    ? savedMappings.filter((mapping) => mapping.setting_id === settingId)
    : savedMappings

  const savedPayoutEntries = findAllSavedPayoutCostsEntries(scopedSaved, settingId)
  const debitSlots = collectDebitAccountSlots({
    mergedSchema,
    erpOrderMappingsData,
    savedPayoutEntries,
    relatedAvailablePlatformId,
  })

  const payoutChildDefaultSchemaCodes = new Set(
    mergedSchema
      .filter((mapping) => mapping.isDefault !== false)
      .map((mapping) => mapping.field_code)
      .filter(Boolean)
  )
  const shouldAddSavedPayoutDefaultMapping = (fieldCode?: string | null) =>
    Boolean(fieldCode && payoutChildDefaultSchemaCodes.has(fieldCode))

  let result = [...mergedSchema]

  scopedSaved
    .filter((mapping) => !mapping.source_field && mapping.destination_field)
    .forEach((mapping) => {
      const fieldCode = mapping.destination_field.api_code
      if (!shouldAddSavedPayoutDefaultMapping(fieldCode)) {
        return
      }

      const slotId = mapping.setting_mapping_id ?? null
      const alreadyExists = result.some(
        (existing) =>
          existing.field_code === fieldCode &&
          existing.isDefault !== false &&
          (slotId === null ||
            `${existing.setting_mapping_id ?? existing.id ?? ''}` === `${slotId}`)
      )
      if (alreadyExists) {
        return
      }

      result.push({
        field_code: fieldCode,
        api_code: fieldCode,
        display_name: mapping.destination_field.display_name,
        isDefault: mapping.isDefault !== false,
        isRequired: false,
        setting_mapping_id: slotId,
      })
    })

  if (!result.some((mapping) => mapping.field_code === 'PAYOUT_COSTS') && payoutFieldMeta) {
    const payoutMeta = buildPayoutCostsMappingMeta(payoutFieldMeta)
    if (payoutMeta) {
      result.push(payoutMeta)
    }
  }

  return attachPayoutCostsMappings({
    mappings: dedupePayoutFieldMappingsByCode(result),
    debitSlots,
    savedPayoutEntries,
    payoutFieldMeta,
  })
}

const payoutChildFieldMappingsCache = new Map<string, PayoutCostsMappingMeta[]>()

export const clearPayoutChildFieldMappingsCache = () => {
  payoutChildFieldMappingsCache.clear()
}

export const buildPayoutChildFieldMappingsCached = (
  params: Parameters<typeof buildPayoutChildFieldMappings>[0]
): PayoutCostsMappingMeta[] => {
  if (isPayoutSchemaAwaitingErpFields(params)) {
    return []
  }

  const cacheKey = [
    params.settingId ?? 'null',
    params.relatedAvailablePlatformId ?? 'null',
    serializeChildFieldMappingsKey(params.schemaFieldMappings),
    serializeChildFieldMappingsKey(params.erpSchemaFieldMappings),
    serializeChildFieldMappingsKey(params.erpOrderMappingsData),
    serializeSavedPayoutMappingsKey(params.savedMappings, params.settingId),
    params.payoutFieldMeta?.field_code ?? '',
  ].join('::')

  const cached = payoutChildFieldMappingsCache.get(cacheKey)
  if (cached) {
    return cached
  }

  const resolved = buildPayoutChildFieldMappings(params)
  payoutChildFieldMappingsCache.set(cacheKey, resolved)
  return resolved
}

export const buildPayoutDependentDestinationMappings = ({
  nonDefaultFieldMappings = [],
  savedMappings = [],
  settingId = null,
}: {
  nonDefaultFieldMappings?: PayoutCostsMappingMeta[]
  savedMappings?: SECTION_WISE_SAVED_FIELD_MAPPING[] | []
  settingId?: number | null
}): PayoutCostsMappingMeta[] => {
  const debitFromSchema = nonDefaultFieldMappings.filter(
    (mapping) =>
      isPayoutPairDestinationField(mapping.field_code ?? mapping.api_code) ||
      (mapping.field_code === 'PAYOUT_COSTS' && mapping.map_with)
  )

  const debitSlots = debitFromSchema
    .map((mapping) => {
      if (isPayoutPairDestinationField(mapping.field_code ?? mapping.api_code)) {
        return mapping
      }
      return mapping.map_with
    })
    .filter(Boolean) as PayoutCostsMappingMeta[]

  if (debitSlots.length) {
    return dedupePayoutFieldMappingsByCode(debitSlots)
  }

  const savedEntries = findAllSavedPayoutCostsEntries(savedMappings, settingId)
  return collectDebitAccountSlots({
    mergedSchema: [],
    savedPayoutEntries: savedEntries,
  })
}

export const shouldRenderPayoutDependentMapping = ({
  isOrderPayoutChild = false,
  payoutCostsMapping = null,
  savedMappings = [],
  settingId = null,
  nonDefaultFieldMappings = [],
}: {
  isOrderPayoutChild?: boolean
  payoutCostsMapping?: PayoutCostsMappingMeta | null
  savedMappings?: SECTION_WISE_SAVED_FIELD_MAPPING[] | []
  settingId?: number | null
  nonDefaultFieldMappings?: PayoutCostsMappingMeta[]
}) => {
  if (!isOrderPayoutChild) return false
  if (
    !payoutCostsMapping &&
    findAllSavedPayoutCostsEntries(savedMappings, settingId).length === 0
  ) {
    return false
  }
  return buildPayoutDependentDestinationMappings({
    nonDefaultFieldMappings,
    savedMappings,
    settingId,
  }).length > 0
}

export const buildSecondaryOrderMappingsData = ({
  platformSettings = [],
  primaryPlatformSettings = [],
  savedMappings = [],
  settingId = null,
}: {
  platformSettings?: any[]
  primaryPlatformSettings?: any[]
  savedMappings?: SECTION_WISE_SAVED_FIELD_MAPPING[] | []
  settingId?: number | null
}): PayoutCostsMappingMeta[] => {
  const orderMappings =
    platformSettings.find((platformSetting) => platformSetting.group_code === 'ORDER')
      ?.mappings_data ??
    platformSettings.find((platformSetting) => platformSetting.group_code === 'ERP_ORDER')
      ?.mappings_data ??
    []

  if (findPayoutCostsInOrderMappings(orderMappings)) {
    return orderMappings
  }

  const payoutFieldMeta = primaryPlatformSettings
    .find((platformSetting) => platformSetting.group_code === 'ORDER')
    ?.children?.find((childSetting) => childSetting.name === 'isImportOrderPayout')
    ?.field_mappings?.find((fieldMapping) => fieldMapping.field_code === 'PAYOUT_COSTS')

  const resolvedPayoutCostsMapping = resolvePayoutCostsMapping({
    orderMappings,
    savedMappings,
    payoutFieldMeta,
    settingId,
  })

  if (!resolvedPayoutCostsMapping) {
    return orderMappings
  }

  return [...orderMappings, resolvedPayoutCostsMapping]
}
